
import React, { useEffect, useMemo, memo, useState } from "react";
import {
  DndContext,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToHorizontalAxis,
} from "@dnd-kit/modifiers";

import { useCommonSensors, defaultCollisionDetection } from "../lib/adapter";
import { SortableItem } from "./SortableItem";
import { arrayMove } from "../utils/array";

export function SortableListComponent({ initialItems = [], orientation = "vertical",  getSortedData = () => {} }) {
  const [items, setItems] = useState(initialItems);
  const [activeId, setActiveId] = useState(null);

  useEffect(()=> {
    setItems(initialItems);
  }, [initialItems]);
  
  useEffect(()=> {
    getSortedData(items);
  }, [items]);

  const sensors = useCommonSensors();
  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const strategy = useMemo(() => {
    if (orientation === "vertical") return verticalListSortingStrategy;
    if (orientation === "horizontal") return horizontalListSortingStrategy;
    return rectSortingStrategy;
  }, [orientation]);

  const modifiers = useMemo(() => {
    return orientation === "vertical"
      ? [restrictToVerticalAxis]
      : [restrictToHorizontalAxis];
  }, [orientation]);

  function onDragStart(event) {
    setActiveId(event?.active?.id ?? null);
  }

  function onDragEnd(event) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !active) return;

    if (active.id !== over.id) {
      setItems((prev) => {
        const oldIndex = prev.findIndex((i) => i.id === active.id);
        const newIndex = prev.findIndex((i) => i.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }

  function onDragCancel() {
    setActiveId(null);
  }

  const activeItem = items.find((i) => i.id === activeId) || null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={defaultCollisionDetection}
      modifiers={modifiers}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={ids} strategy={strategy}>
        <div
          role="list"
          aria-orientation={orientation}
          style={{
            display: "flex",
            flexDirection: orientation === "vertical" ? "column" : "row",
            flexWrap: orientation === "horizontal" ? "wrap" : "nowrap",
          }}
        >
          {items.map((item) => (
            <SortableItem key={item.id} id={item.id} label={item.title} desc={item.cardSubTitle} />
          ))}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <div>
            {activeItem.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export const SortableList = memo(SortableListComponent);